import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from 'lucide-react';
import { HubHeader } from '../components/hub/HubHeader';
import { FilterBar } from '../components/hub/FilterBar';
import { DirectoryColumns } from '../components/hub/DirectoryColumns';
import { HubFaqs } from '../components/hub/HubFaqs';
import { SiblingHubs } from '../components/hub/SiblingHubs';
import { TierNote } from '../components/TierNote';
import { hubFaqs, hubFilters, tier1Parks, tier2Parks, tier3Parks } from '../data/places';
export function ParksHub() {
  const [active, setActive] = useState<string[]>([]);
  function toggle(option: string) {
    setActive(prev => prev.includes(option) ? prev.filter(x => x !== option) : [...prev, option]);
  }
  const filtered = useMemo(() => {
    if (active.length === 0) return tier2Parks;
    return tier2Parks.filter(p => active.every(a => p.tags.includes(a) || p.area === a || p.ages.includes(a)));
  }, [active]);
  return <main id="main" className="bg-paper" data-id="element-945" data-mp="2vumry9kbbbfq">
      <HubHeader crumb="Parks & playgrounds" meta="41 parks · Updated August 2026" titleLead="Best parks & playgrounds in" titleItalic="Santa Barbara" deck="Every park below has been visited with kids in tow. We note the four things that actually decide a morning — shade, restrooms, whether it’s fenced, and how bad the parking is." data-id="element-946" data-mp="30s6j2a23zip3" />

      <FilterBar filters={hubFilters} active={active} onToggle={toggle} data-id="element-947" data-mp="3epuzn5p583oc" />

      <section aria-labelledby="tier1-h" className="bg-paper" data-id="element-948" data-mp="3jpg3nuj6727x">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-949" data-mp="3oh7m1hs6a1mz">
          <TierNote tier="1" label="Full place pages" note="Four to six per hub. Each has its own URL, photos and 400–800 words." data-id="element-950" data-mp="3tgu5wa2iag4r" />
          <h2 id="tier1-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-951" data-mp="28g34wmzdxsv">
            Start here — our four favorites
          </h2>

          <ul className="mt-16 grid gap-x-16 gap-y-20 lg:grid-cols-2" data-id="element-952" data-mp="1ydnt5kpb7fj8">
            {tier1Parks.map(park => <li key={park.slug} className="group relative" data-id="element-953" data-mp="2c4u0u42qsjh7">
                <div className="overflow-hidden bg-creme" data-id="element-954" data-mp="2c76nuvncxk14">
                  <img src={park.image} alt={park.imageAlt} className="photo-soft aspect-[4/3] w-full object-cover transition-transform duration-300 ease-soft group-hover:scale-[1.03]" data-id="element-955" data-mp="27dat022e4da6" />
                </div>
                <p className="label mt-7 text-olive" data-id="element-956" data-mp="2v47wcqg4e29t">{park.area}</p>
                <h3 className="mt-3 font-display text-[32px] font-300 leading-tight text-ink" data-id="element-957" data-mp="2r2cw7p9xduxm">
                  <Link to="/place" className="decoration-olive decoration-1 underline-offset-[7px] after:absolute after:inset-0 after:content-[''] group-hover:underline" data-id="element-958" data-mp="2r0iboc2kg4u2">
                    {park.name}
                  </Link>
                </h3>
                <p className="mt-4 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-959" data-mp="qlyux574l5hn">
                  {park.hook}
                </p>
                <dl className="mt-7 grid grid-cols-4 gap-x-6 border-t border-line pt-5" data-id="element-960" data-mp="ctiri3abb5g7">
                  {[['Ages', park.ages], ['Shade', park.shade], ['Restrooms', park.restrooms], ['Parking', park.parking]].map(([term, value]) => <div key={term} data-id="element-961" data-mp="7te7w4e3xulr">
                      <dt className="label text-muted/70" data-id="element-962" data-mp="3q1n4tc437mll">{term}</dt>
                      <dd className="mt-1.5 text-[13.5px] leading-snug text-ink" data-id="element-963" data-mp="3q3gfr3lqgsze">
                        {value}
                      </dd>
                    </div>)}
                </dl>
              </li>)}
          </ul>
        </div>
      </section>

      <section aria-label="Sponsored message" className="bg-creme/60" data-id="element-964" data-mp="1t823wfg1gra3">
        <div className="mx-auto flex max-w-shell flex-wrap items-end justify-between gap-8 px-6 py-14 lg:px-10" data-id="element-965" data-mp="3pand0d9l6h5k">
          <div className="max-w-xl" data-id="element-966" data-mp="3kderq3t55x03">
            <p className="label text-ink/55" data-id="element-967" data-mp="3fdse3t9eieqr">Sponsored by a local business</p>
            <h2 className="mt-4 font-display text-[30px] font-300 leading-tight text-ink" data-id="element-968" data-mp="3ffvg7bntvybu">
              Birthday parties at Tucker’s Grove, booked in one call
            </h2>
            <p className="mt-3 text-[15px] leading-[1.8] text-ink/70" data-id="element-969" data-mp="36eoveqe5c1cs">
              Group areas, tables and cleanup handled. Weekend slots open ninety
              days ahead and go fast in spring.
            </p>
          </div>
          <a href="#faq" className="label shrink-0 border border-ink/30 px-7 py-3.5 text-ink transition-colors duration-150 ease-soft hover:bg-ink hover:text-paper" data-id="element-970" data-mp="2wkya1udbg71b">
            Check availability
          </a>
        </div>
      </section>

      <section aria-labelledby="tier2-h" className="border-t border-line bg-white" data-id="element-971" data-mp="2io1j7sx76pkf">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-972" data-mp="y4pgf7gmf4pe">
          <TierNote tier="2" label="Rich rows, no page of their own" note="Ten minutes each. Most places live here forever, and that’s fine." data-id="element-973" data-mp="133smmv8c7gyy" />
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-974" data-mp="1c5s4fpr6ep2y">
            <h2 id="tier2-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-975" data-mp="fezm0lgrsky0">
              More parks worth knowing
            </h2>
            <p className="label text-muted" role="status" data-id="element-976" data-mp="of5wrzl336th">
              {filtered.length} {filtered.length === 1 ? 'park' : 'parks'}
              {active.length > 0 && ` · ${active.length} filters`}
            </p>
          </div>

          {filtered.length === 0 ? <div className="mt-14 max-w-lg" data-id="element-977" data-mp="3rujyrjjed4qz">
              <h3 className="font-display text-[30px] font-300 text-ink" data-id="element-978" data-mp="p1srr3zh51y">
                Nothing matches all of that
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-979" data-mp="13ywepms4hcjw">
                Try loosening one filter — the area is usually the one worth
                giving up.
              </p>
              <button type="button" onClick={() => setActive([])} className="label mt-8 bg-ink px-7 py-3.5 text-paper transition-colors duration-150 ease-soft hover:bg-olive" data-id="element-980" data-mp="1csjave08shtm">
                Clear filters
              </button>
            </div> : <ul className="mt-12 border-t border-line" data-id="element-981" data-mp="u2tg2qwhbw1p">
              {filtered.map(park => <li key={park.id} data-id="element-982" data-mp="p3q9h20uphrm">
                  <article className="grid gap-y-5 border-b border-line py-9 lg:grid-cols-[1.6fr_1fr] lg:gap-x-16" data-id="element-983" data-mp="p2owgs09yl79">
                    <div data-id="element-984" data-mp="25gows4318cv1">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-985" data-mp="25k2vn2chm3xq">
                        <h3 className="font-display text-[28px] font-300 leading-tight text-ink" data-id="element-986" data-mp="20inc5epyk5hg">
                          {park.name}
                        </h3>
                        <span className="label text-olive" data-id="element-987" data-mp="2d2k9fkrec4wi">{park.area}</span>
                      </div>
                      <p className="mt-4 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-988" data-mp="1z2rm7zf2w6ta">
                        {park.blurb}
                      </p>
                      <a href="#faq" className="label mt-5 inline-flex items-center gap-1.5 text-ink underline decoration-olive underline-offset-4" data-id="element-989" data-mp="2rqf1r01hs792">
                        Park website
                        <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-990" data-mp="2i02l3iwssy87" />
                      </a>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 self-start sm:grid-cols-4 lg:grid-cols-2" data-id="element-991" data-mp="2mxc86rwpsxsv">
                      {[['Ages', park.ages], ['Cost', park.cost], ['Shade', park.shade], ['Restrooms', park.restrooms]].map(([term, value]) => <div key={term} data-id="element-992" data-mp="1alp1ki8u9rdl">
                          <dt className="label text-muted/70" data-id="element-993" data-mp="15nf8cd6od0um">{term}</dt>
                          <dd className="mt-1.5 text-[14px] text-ink" data-id="element-994" data-mp="15lkl2s7xkwnd">{value}</dd>
                        </div>)}
                    </dl>
                  </article>
                </li>)}
            </ul>}
        </div>
      </section>

      <DirectoryColumns title="Every other park in the county" groups={tier3Parks} data-id="element-995" data-mp="1teauousd33uh" />
      <HubFaqs faqs={hubFaqs} data-id="element-996" data-mp="1pacwcmav1x2h" />
      <SiblingHubs exclude={['Parks & playgrounds']} data-id="element-997" data-mp="1pdqzhwrcmaaz" />
    </main>;
}